import React, { useCallback, useEffect, useState } from 'react';
import { dismissMailProblem, getMailLedger, lookAgainMail, retryMailProblem } from './api';
import IntakeAddresses from './IntakeAddresses';

const RESULT = {
  invoice: { label: 'Invoice', cls: 'bg-emerald-100 text-emerald-800' },
  waiting: { label: 'Being read', cls: 'bg-sky-100 text-sky-800' },
  set_aside: { label: 'Set aside', cls: 'bg-amber-100 text-amber-800' },
  error: { label: 'Problem', cls: 'bg-red-100 text-red-800' },
  other: { label: 'Already handled', cls: 'bg-slate-100 text-slate-700' },
};
const STATE_TEXT = {
  ingested: 'received', duplicate: 'already received earlier', quarantined: 'could not tell which business it is for',
  no_invoice_attachment: 'no invoice file in it', error: 'failed, will retry on the next check',
  set_aside: 'set aside: the supplier check failed', reoffered: 'will be checked again on the next check',
};

function ago(iso) {
  if (!iso) return null;
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (Number.isNaN(mins)) return null;
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 36) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} days ago`;
}

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * Email intake, kept out of the way: one quiet line (collapsed), with anything that needs a person pulled out on top.
 * Emails that were not invoices are only counted. A problem (an invoice email that could not be handled or read) is
 * flagged with Try again / Dismiss; the bell tells people too.
 */
export default function MailboxPanel({ tick, onChanged, onStatus }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [lookedAgain, setLookedAgain] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await getMailLedger(7));
      setError('');
    } catch (e) {
      if (e.status === 403 || e.status === 404) setHidden(true);
      else setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (key, fn) => {
    setBusy(key);
    setError('');
    try {
      await fn(key);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  // Owners and admins: the week's skipped emails go back to intake (for example after adding a supplier's sender).
  const lookAgain = async () => {
    setBusy('look-again');
    setError('');
    try {
      const res = await lookAgainMail({ days: 7 });
      setLookedAgain(res?.reoffered ?? 0);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  // A mailbox that was working and has stopped needs a person; one that was never set up is only quiet.
  const shown = !hidden && Boolean(data || error);
  const attention = shown && Boolean(error || data?.problems?.length || (data?.stale && data?.last_run_at));
  useEffect(() => { onStatus?.({ shown, attention }); }, [onStatus, shown, attention]);

  if (hidden || (!data && !error)) return null;
  const s = data?.summary || {};
  const problems = data?.problems || [];
  const checked = ago(data?.last_run_at);
  const quiet = [
    checked ? `Checked ${checked}` : 'Not checked yet',
    `${s.invoice || 0} invoice${s.invoice === 1 ? '' : 's'} this week`,
    data?.skipped ? `${data.skipped} other email${data.skipped === 1 ? '' : 's'} skipped` : null,
  ].filter(Boolean).join(' · ');

  return (
    <section className="space-y-2" data-testid="mailbox-panel">
      {problems.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2" role="alert" data-testid="mailbox-problems">
          <h2 className="text-sm font-semibold text-red-900">
            {problems.length === 1 ? 'An invoice email needs attention' : `${problems.length} invoice emails need attention`}
          </h2>
          <ul className="space-y-2">
            {problems.map((p) => (
              <li key={p.key} className="text-sm flex flex-wrap items-start gap-2" data-testid={`mailbox-problem-${p.key.slice(0, 12)}`}>
                <div className="flex-1 min-w-[220px]">
                  <p className="text-ink">{p.subject || '(no subject)'}{p.sender ? <span className="text-ink-muted"> · {p.sender}</span> : null}</p>
                  <p className="text-red-900">{p.message}</p>
                </div>
                {p.can_retry && (
                  <button type="button" disabled={busy === p.key} data-testid={`mailbox-retry-${p.key.slice(0, 12)}`}
                    className="px-3 py-1.5 rounded-lg bg-surface border border-surface-border text-ink hover:bg-surface-sunken disabled:opacity-50"
                    onClick={() => act(p.key, retryMailProblem)}>Try again</button>
                )}
                <button type="button" disabled={busy === p.key} data-testid={`mailbox-dismiss-${p.key.slice(0, 12)}`}
                  className="px-3 py-1.5 rounded-lg text-ink-muted hover:text-ink hover:underline disabled:opacity-50"
                  onClick={() => act(p.key, dismissMailProblem)}>I've handled it</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="text-sm text-red-700">{error}</p>}
      <details className="rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm" data-testid="mailbox-details">
        <summary className="cursor-pointer text-ink-muted select-none" data-testid="mailbox-summary">
          <span className="text-ink">Email intake</span> · {quiet}
          {data?.stale && <span className="text-amber-700" data-testid="mailbox-stale-flag"> · not checked recently</span>}
        </summary>
        <div className="mt-3 space-y-3">
          <IntakeAddresses />
          {data?.stale && (
            <p className="text-amber-800" data-testid="mailbox-stale">
              {data.last_run_at
                ? `New invoice emails are not being picked up: the mailbox was last checked ${checked}. Check the email workflow is running.`
                : 'The mailbox has never been checked. Set up and activate the email workflow to receive emailed invoices.'}
            </p>
          )}
          {data?.runs?.[0] && (
            <p className="text-xs text-ink-muted" data-testid="mailbox-last-run">
              Last check: {data.runs[0].listed} emails in the mailbox, {data.runs[0].already_done} handled before,{' '}
              {data.runs[0].to_process} handled now{data.runs[0].retry ? ` (${data.runs[0].retry} retried)` : ''}.
              {data.runs[0].unaccounted > 0 && (
                <span className="text-red-700" data-testid="mailbox-unaccounted">
                  {' '}{data.runs[0].unaccounted} did not finish and will be retried.
                </span>
              )}
            </p>
          )}
          {data?.emails?.length > 0 ? (
            <ul className="divide-y divide-slate-100" data-testid="mailbox-emails">
              {data.emails.map((e) => <EmailRow key={e.key} email={e} />)}
            </ul>
          ) : <p className="text-ink-muted">No invoice emails this week.</p>}
          {data?.skipped > 0 && (
            <p className="text-xs text-ink-muted" data-testid="mailbox-skipped">
              {data.skipped} other email{data.skipped === 1 ? '' : 's'} in the mailbox were not invoices and were skipped.
              {data.can_look_again && (
                <>
                  {' '}
                  <button type="button" disabled={busy === 'look-again'} data-testid="mailbox-look-again"
                    className="text-ink underline hover:no-underline disabled:opacity-50"
                    onClick={lookAgain}>Look again</button>
                </>
              )}
            </p>
          )}
          {lookedAgain !== null && (
            <p className="text-xs text-ink" data-testid="mailbox-look-again-done">
              {lookedAgain} email{lookedAgain === 1 ? '' : 's'} will be checked again on the next mailbox check.
            </p>
          )}
        </div>
      </details>
    </section>
  );
}

function EmailRow({ email }) {
  const r = RESULT[email.result] || RESULT.other;
  const inv = email.documents?.map((d) => d.invoice).find(Boolean);
  const trail = [STATE_TEXT[email.state] || email.state,
    ...(email.documents || []).map((d) => (d.invoice
      ? `invoice ${d.invoice.invoice_no || ''} (${(d.invoice.state || '').replace(/_/g, ' ')})`
      : `${d.filename}: ${(d.status || '').replace(/_/g, ' ')}${d.triage_reason ? ` (${d.triage_reason})` : ''}`)),
  ].join(' → ');
  return (
    <li className="py-2 flex flex-wrap items-start gap-2" data-testid={`mailbox-email-${email.key.slice(0, 12)}`}>
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.cls}`}>{r.label}</span>
      <div className="flex-1 min-w-0">
        <p className="text-ink truncate">
          {email.subject || '(no subject)'}
          {email.sender ? <span className="text-ink-muted"> · {email.sender}</span> : null}
        </p>
        <p className="text-ink-muted">{trail}{inv?.totals?.invoice_total ? ` · £${inv.totals.invoice_total}` : ''}</p>
      </div>
      <span className="text-xs text-ink-muted">{when(email.received_at || email.first_seen_at)}</span>
    </li>
  );
}
